import { useState } from 'react';
import {
  validateAddress,
  getStateAbrev,
  getStateFormatted,
} from 'helpers/signup.helpers';
import useDirtyData from 'pages/Profile/pages/ProfileOverview/hooks/useDirtyData';
import { useGoogleAutocomplete } from 'partial/GoogleAutocomplete/hooks/googleAutocomplete.hook';
import { useLogger } from 'hook/error.hook';
import { useConfig } from './config.hook';

export const useAddressForm = (
  initAddress = {
    street: null,
    unit: null,
    city: null,
    state: null,
    zip: null,
  },
  disableAutoComplete,
  usePOBoxValidation = false,
) => {
  const {
    street: initStreet,
    unit: initUnit,
    city: initCity,
    state: initState,
    zip: initZip,
  } = initAddress;
  const [street, setStreet] = useState(initStreet);
  const [streetError, setStreetError] = useState(null);
  const [unit, setUnit] = useState(initUnit);
  const [city, setCity] = useState(initCity);
  const [cityError, setCityError] = useState(null);
  const [state, setUSState] = useState(initState);
  const [displayState, setDisplayState] = useState(
    getStateFormatted(initState),
  );
  const [stateError, setStateError] = useState(null);
  const [zip, setZip] = useState(initZip);
  const [zipError, setZipError] = useState(null);
  const [addressError, setAddressError] = useState(null);
  const [addressErrorMessage, setAddressErrorMessage] = useState(null);
  const [editAddress, setEditAddress] = useState(false);
  const dirtyData = useDirtyData({}, true);
  const logger = useLogger({
    name: 'addressForm.hook',
  });

  const { config } = useConfig();
  const { enableRecurlyBillingAddress } = config ?? {};

  const handleCityChange = value => {
    const regex = /[0-9!@#$%^&*(){}\][\\_+='";:<,>./?`~|]/gi;
    const city = value.replace(regex, '');
    setCity(city);
    setCityError(null);

    dirtyData.processDirtyData('city', city, initCity);
  };

  const handleUSStateChange = value => {
    const abbrevVal = getStateAbrev(value);
    setUSState(abbrevVal);
    setDisplayState(value);

    setStateError(null);

    dirtyData.processDirtyData('state', abbrevVal, initState);
  };

  const handleZipChange = value => {
    const regex = /[^0-9]/gi;
    const zip = value.replace(regex, '');
    setZip(zip);
    setZipError(null);

    dirtyData.processDirtyData('zip', zip, initZip);
  };

  const handleChange = e => {
    e.preventDefault();
    let { name, value } = e.target;

    switch (name) {
      case 'street':
        setStreetError(null);
        dirtyData.processDirtyData(name, value, initStreet);
        return setStreet(value);
      case 'unit':
        dirtyData.processDirtyData(name, value, initUnit);
        return setUnit(value);
      case 'city':
        setCityError(null);
        return handleCityChange(value);
      case 'zip':
        setZipError(null);
        return handleZipChange(value);
      default:
        break;
    }
  };

  const isMissingData = () => {
    if (street && city && state && zip) {
      return false;
    } else return true;
  };

  const isAddressValid = valuesToValidate => {
    const errors = validateAddress(
      valuesToValidate
        ? valuesToValidate
        : {
            street: street,
            city: city,
            state: state,
            zip: zip,
          },
      usePOBoxValidation,
    );
    if (errors) {
      for (let [key, value] of Object.entries(errors)) {
        switch (key) {
          case 'street':
            setStreetError(value);
            break;
          case 'state':
            setStateError(value);
            break;
          case 'city':
            setCityError(value);
            break;
          case 'zip':
            setZipError(value);
            break;
          default:
            console.warn('Address error case missing:', key, ':', value);
            break;
        }
      }
      return false;
    }
    return true;
  };

  const getFullAddress = forceIncludeUnit => {
    let address = {
      street: street,
      city: city,
      state: state,
      zip: zip,
    };

    if (forceIncludeUnit || unit) {
      address.unit = unit;
    }
    return address;
  };

  const handleAddressError = err => {
    if (err.code === 'SU_1009' || err.code === 'SU_1015') {
      setZipError(true);
      setStateError(true);
    }
    setAddressError(err);
    setAddressErrorMessage(err.message);
    logger.reportAPIError(err);
  };

  const clearError = () => {
    setAddressError(null);
    setAddressErrorMessage(null);
    setZipError(null);
    setStateError(null);
    setStreetError(null);
    setCityError(null);
  };

  const clearForm = () => {
    clearError();
    setStreet(initStreet);
    setUnit(initUnit);
    setCity(initCity);
    setUSState(initState);
    setDisplayState(getStateFormatted(initState));
    setZip(initZip);
  };

  const updateAddress = address => {
    setStreet(address.street);
    setUnit(address.unit);
    setCity(address.city);
    updateUSState(address.state);
    setZip(address.zip);
  };

  const updateUSState = usState => {
    let displayState = getStateFormatted(usState);
    let state = null;
    if (displayState) {
      state = usState;
    } else {
      const stateAbbrev = getStateAbrev(usState);
      if (stateAbbrev) {
        displayState = usState;
        state = stateAbbrev;
      }
    }
    setUSState(state);
    setDisplayState(displayState);
  };

  /* Google autocomplete */

  const autofillHandler = input => {
    clearError();
    updateAddress(input);
  };

  const googleAutocomplete = useGoogleAutocomplete(
    autofillHandler,
    disableAutoComplete,
  );

  const handleEditAddress = value => {
    setEditAddress(value);
  };

  const handleBillingAddressUpdate = () => {
    clearForm();
    handleEditAddress(false);
  };

  return {
    street,
    streetError,
    unit,
    city,
    cityError,
    displayState,
    state,
    stateError,
    zip,
    zipError,
    addressError,
    addressErrorMessage,
    handleAddressError,
    clearError,
    isMissingData,
    isAddressValid,
    handleChange,
    handleUSStateChange,
    getFullAddress,
    dirtyDataEmpty: dirtyData.isEmpty(),
    clearForm,
    updateAddress,
    googleAutocomplete,
    editAddress,
    handleEditAddress,
    enableRecurlyBillingAddress,
    handleBillingAddressUpdate,
  };
};
